import { Modal, ModalOverlay, ModalContent, ModalBody } from '@chakra-ui/modal';
import InputField from 'components/fields/InputField';
import { MdClose } from 'react-icons/md';
import { useState } from 'react';

const WebhookModal = (props: {
    isOpen: boolean;
    onClose: () => void;
    onSave: (url: string) => void;
    onTest: (url: string) => Promise<{ success: boolean; message: string }>;
    loading?: boolean;
}) => {
    const { isOpen, onClose, onSave, onTest, loading } = props;
    const [url, setUrl] = useState('');
    const [urlError, setUrlError] = useState('');
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

    const isValidUrl = (value: string) => {
        try {
            const u = new URL(value);
            return u.protocol === 'https:' || u.protocol === 'http:';
        } catch {
            return false;
        }
    };

    const handleSave = () => {
        if (!isValidUrl(url.trim())) {
            setUrlError('Veuillez entrer une URL valide (ex: https://...)');
            return;
        }
        setUrlError('');
        onSave(url.trim());
    };

    const handleTest = async () => {
        if (!isValidUrl(url.trim())) {
            setUrlError('Veuillez entrer une URL valide (ex: https://...)');
            return;
        }
        setUrlError('');
        setTesting(true);
        setTestResult(null);
        try {
            const result = await onTest(url.trim());
            setTestResult(result);
        } finally {
            setTesting(false);
        }
    };

    const handleClose = () => {
        setUrl('');
        setUrlError('');
        setTestResult(null);
        setTesting(false);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} scrollBehavior="inside">
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="!z-[1002] !m-auto !w-max min-w-[350px] !max-w-[85%] border-none p-0 shadow-none focus:outline-none md:top-[12vh]">
                <ModalBody>
                    <div className="relative flex flex-col rounded-2xl border border-leadrs-stroke bg-white px-8 py-8 !z-[1004] max-w-[550px]">
                        <button
                            className="absolute right-5 top-5 text-leadrs-text-soft hover:text-leadrs-text transition-colors"
                            onClick={handleClose}
                        >
                            <MdClose size={20} />
                        </button>

                        <h2 className="font-crimson text-2xl font-semibold text-leadrs-text">
                            Configurer un Webhook
                        </h2>
                        <p className="mt-2 mb-6 font-geist text-sm text-leadrs-text-soft">
                            Leadrs enverra les données du lead en JSON (POST) à cette URL dès réception d'un nouveau lead.
                        </p>

                        <InputField
                            label="URL Webhook"
                            placeholder="https://votre-serveur.com/webhook"
                            id="webhookUrl"
                            type="url"
                            defaultValue={url}
                            onChange={(e: any) => {
                                setUrl(e.target.value);
                                setUrlError('');
                                setTestResult(null);
                            }}
                            extra="mb-2"
                        />
                        {urlError && (
                            <p className="mb-4 font-geist text-xs text-leadrs-red-focus">{urlError}</p>
                        )}

                        <div className="mb-6">
                            <p className="mb-2 font-geist text-xs text-leadrs-text-soft">Payload envoyé (JSON) :</p>
                            <pre className="overflow-x-auto rounded-xl border border-leadrs-stroke bg-leadrs-bg p-3 font-geist text-xs text-leadrs-text">{`{
  "timestamp": "2026-03-06T10:00:00.000Z",
  "answers": [
    { "field": "email", "answer": "jean@exemple.fr" },
    { "field": "telephone", "answer": "0612345678" },
    { "field": "prenom", "answer": "Jean" },
    { "field": "nom", "answer": "Dupont" }
  ]
}`}</pre>
                        </div>

                        <button
                            className="mb-3 flex h-10 w-full items-center justify-center rounded-xl border border-leadrs-brand font-geist text-sm font-medium text-leadrs-brand hover:bg-leadrs-bg transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={handleTest}
                            disabled={!url.trim() || testing || loading}
                        >
                            {testing ? 'Test en cours...' : "Tester l'intégration"}
                        </button>
                        {testResult && (
                            <p
                                className={`mb-4 font-geist text-xs ${
                                    testResult.success ? 'text-leadrs-green-focus' : 'text-leadrs-red-focus'
                                }`}
                            >
                                {testResult.message}
                            </p>
                        )}

                        <div className="flex gap-3">
                            <button
                                className="flex h-10 flex-1 items-center justify-center rounded-xl border border-leadrs-stroke font-geist text-sm font-medium text-leadrs-text hover:bg-leadrs-bg transition-colors disabled:opacity-50"
                                onClick={handleClose}
                                disabled={loading}
                            >
                                Annuler
                            </button>
                            <button
                                className="flex h-10 flex-1 items-center justify-center rounded-xl bg-leadrs-brand font-geist text-sm font-medium text-white hover:opacity-80 transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
                                onClick={handleSave}
                                disabled={!url.trim() || loading}
                            >
                                {loading ? 'Enregistrement...' : 'Enregistrer'}
                            </button>
                        </div>
                    </div>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default WebhookModal;
